const PRODUCTION_API_URL = 'https://ethiccheck-production-6ade.up.railway.app';

/** CRA bakes REACT_APP_API_URL at build time; fall back to prod URL when hosted off localhost. */
export function getApiBaseUrl(): string {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host !== 'localhost' && host !== '127.0.0.1') {
      return PRODUCTION_API_URL;
    }
  }
  return 'http://localhost:3001';
}

const API_BASE_URL = getApiBaseUrl();

const REQUEST_TIMEOUT_MS = 30_000;

async function fetchWithTimeout(
  url: string,
  init?: RequestInit
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out after 30 seconds');
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

// BDS Category Types
export type BdsCategory = 
  | 'economic_exploitation'
  | 'exploitation_occupied_resources'
  | 'settlement_enterprise'
  | 'israeli_construction_occupied_land'
  | 'services_to_settlements'
  | 'other_bds_activities';

export interface BdsCategoryStatus {
  category: BdsCategory;
  status: 'pass' | 'review' | 'excluded';
  evidence?: string[];
}

export interface BdsStatus {
  overall: 'pass' | 'review' | 'excluded';
  categories?: BdsCategoryStatus[];
}

export type PortfolioLens = 'bds' | 'defense' | 'surveillance' | 'shariah';

export type EvidenceSummaries = Partial<Record<PortfolioLens, string>>;

export interface PortfolioPosition {
  symbol: string;
  valueUsd?: number;
  shares?: number;
  priceUsd?: number;
}

export interface ScreeningRequest {
  symbols: string[];
  positions?: PortfolioPosition[];
  filters: {
    bds: {
      enabled: boolean;
      categories?: BdsCategory[];
    };
    defense: boolean;
    surveillance: boolean;
    shariah: boolean;
  };
  options?: {
    lookthrough?: boolean;
    autoDiscover?: boolean;
  };
}

export interface ScreeningResult {
  symbol: string;
  company: string;
  statuses: {
    bds: BdsStatus;
    defense: 'pass' | 'review' | 'excluded';
    surveillance: 'pass' | 'review' | 'excluded';
    shariah: 'pass' | 'review' | 'excluded';
  };
  finalVerdict: 'PASS' | 'REVIEW' | 'EXCLUDED';
  reasons: string[];
  confidence: 'High' | 'Medium' | 'Low';
  asOfRow: string;
  sources: Array<{ label: string; url: string }>;
  auditId: string;
  shariahEstimated?: boolean;
  etfExposure?: Array<{
    lens: PortfolioLens;
    excludedPct: number;
    reviewPct: number;
    topHoldings: Array<{
      symbol: string;
      name: string;
      weightPct: number;
      status: 'excluded' | 'review';
    }>;
  }>;
  isEtfLookthrough?: boolean;
  evidenceSummaries?: EvidenceSummaries;
  lensReasons?: Partial<Record<PortfolioLens, string[]>>;
}

export interface LensExposureUsd {
  excludedUsd: number;
  reviewUsd: number;
  cleanUsd: number;
  excludedPct: number;
  score: number;
}

export interface PortfolioSummary {
  weighting: 'dollar' | 'equal';
  totalValueUsd?: number;
  overallScore: number;
  lensScores: Partial<Record<PortfolioLens, number>>;
  exposureByLens: Partial<Record<PortfolioLens, LensExposureUsd>>;
  etfBreakdown: Array<{
    etfSymbol: string;
    lens: PortfolioLens;
    excludedUsd: number;
    reviewUsd: number;
    topHoldings: Array<{ symbol: string; name: string; weightPct: number }>;
  }>;
}

export interface ScreeningResponse {
  requestId: string;
  asOf: string;
  rows: ScreeningResult[];
  portfolio?: PortfolioSummary;
  warnings: string[];
}

export interface HealthResponse {
  status: string;
  timestamp: string;
  version: string;
}

export interface HoldingsParseResponse {
  requestId: string;
  tickers: string[];
  positions: PortfolioPosition[];
  warnings?: string[];
  asOf?: string;
  rows?: ScreeningResult[];
  portfolio?: PortfolioSummary;
}

export interface PortfolioAskCitation {
  symbol?: string;
  lens?: PortfolioLens;
  excerpt: string;
  sourceUrl?: string;
}

export interface PortfolioAskContext {
  rows: ScreeningResult[];
  portfolio?: PortfolioSummary;
  filters?: ScreeningRequest['filters'];
}

export interface PortfolioAskResponse {
  requestId: string;
  answer: string;
  citations: PortfolioAskCitation[];
  latencyMs: number;
}

// API Functions
export const api = {
  // Health check
  async checkHealth(): Promise<HealthResponse> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/health`);
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.statusText}`);
    }
    return response.json();
  },

  async runScreening(request: ScreeningRequest): Promise<ScreeningResponse> {
    if (!request || !Array.isArray(request.symbols)) {
      throw new Error('Screening failed: symbols must be an array');
    }

    const response = await fetchWithTimeout(`${API_BASE_URL}/api/v1/screen`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      let detail = response.statusText;
      try {
        const body = await response.json();
        if (body && typeof body.error === 'string' && body.error.length > 0) {
          detail = body.error;
        }
      } catch {
        // Response body wasn't JSON; fall back to statusText.
      }
      throw new Error(`Screening failed: ${detail}`);
    }

    return response.json();
  },

  async parseHoldings(
    csvContent: string,
    options?: {
      screen?: boolean;
      filters?: ScreeningRequest['filters'];
      options?: ScreeningRequest['options'];
    }
  ): Promise<HoldingsParseResponse> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/v1/holdings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        csvContent,
        screen: options?.screen ?? false,
        ...(options?.screen
          ? { filters: options.filters, options: options.options }
          : {}),
      }),
    });

    if (!response.ok) {
      let detail = response.statusText;
      try {
        const body = await response.json();
        if (body && typeof body.error === 'string' && body.error.length > 0) {
          detail = body.error;
        }
      } catch {
        // fall back to statusText
      }
      throw new Error(`CSV parse failed: ${detail}`);
    }

    return response.json();
  },

  // Get methodology for a specific filter
  async getMethodology(filter: string): Promise<any> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/v1/methodology/${filter}`);
    if (!response.ok) {
      throw new Error(`Failed to get ${filter} methodology: ${response.statusText}`);
    }
    return response.json();
  },

  async askPortfolio(
    question: string,
    context: PortfolioAskContext
  ): Promise<PortfolioAskResponse> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/v1/portfolio/ask`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        question,
        context: {
          rows: context.rows,
          portfolio: context.portfolio,
          filters: context.filters,
        },
      }),
    });

    if (!response.ok) {
      let detail = response.statusText;
      try {
        const body = await response.json();
        if (body && typeof body.error === 'string' && body.error.length > 0) {
          detail = body.error;
        }
      } catch {
        // fall back to statusText
      }
      throw new Error(
        response.status === 503
          ? 'Portfolio Q&A is not enabled on this server.'
          : `Portfolio Q&A failed: ${detail}`
      );
    }

    return response.json();
  },
};

/** UI verdict labels aligned with API pass / review / excluded */
export type UiVerdict = 'clean' | 'review' | 'block';

export interface ConvertedBdsStatus {
  overall: UiVerdict;
  categories?: Array<{
    category: string;
    status: UiVerdict;
    evidence?: string[];
  }>;
}

export interface ConvertedScreeningRow {
  ticker: string;
  name: string;
  bds: ConvertedBdsStatus;
  defense: UiVerdict;
  shariah: UiVerdict;
  shariahEstimated: boolean;
  verdict: UiVerdict;
  reasons: string[];
  sources: Array<{ label: string; url: string }>;
  rawStatuses: ScreeningResult['statuses'];
  evidenceSummaries?: EvidenceSummaries;
  lensReasons?: Partial<Record<PortfolioLens, string[]>>;
}

// Helper function to convert API response to frontend format
export function convertApiResponse(apiResponse: ScreeningResponse): ConvertedScreeningRow[] {
  return apiResponse.rows.map(row => ({
    ticker: row.symbol,
    name: row.company,
    bds: convertBdsStatus(row.statuses.bds),
    defense: convertStatus(row.statuses.defense),
    shariah: convertStatus(row.statuses.shariah),
    shariahEstimated: row.shariahEstimated ?? false,
    verdict: convertVerdict(row.finalVerdict),
    reasons: row.reasons ?? [],
    sources: row.sources ?? [],
    rawStatuses: row.statuses,
    evidenceSummaries: row.evidenceSummaries,
    lensReasons: row.lensReasons,
  }));
}

function convertStatus(status: 'pass' | 'review' | 'excluded'): UiVerdict {
  switch (status) {
    case 'pass': return 'clean';
    case 'review': return 'review';
    case 'excluded': return 'block';
    default: return 'review';
  }
}

function convertVerdict(verdict: 'PASS' | 'REVIEW' | 'EXCLUDED'): UiVerdict {
  switch (verdict) {
    case 'PASS': return 'clean';
    case 'REVIEW': return 'review';
    case 'EXCLUDED': return 'block';
    default: return 'review';
  }
}

function convertBdsStatus(bdsStatus: BdsStatus): ConvertedBdsStatus {
  return {
    overall: convertStatus(bdsStatus.overall),
    categories: bdsStatus.categories?.map(cat => ({
      category: getCategoryLabel(cat.category),
      status: convertStatus(cat.status),
      evidence: cat.evidence
    }))
  };
}

function getCategoryLabel(category: BdsCategory): string {
  switch (category) {
    case 'economic_exploitation':
      return 'Economic Exploitation';
    case 'exploitation_occupied_resources':
      return 'Exploitation of Occupied Production and Resources';
    case 'settlement_enterprise':
      return 'Settlement Enterprise';
    case 'israeli_construction_occupied_land':
      return 'Israeli Construction on Occupied Land';
    case 'services_to_settlements':
      return 'Services to the Settlements';
    case 'other_bds_activities':
      return 'Other BDS Activities';
    default:
      return 'Unknown Category';
  }
}
